/* import __COLOCATED_TEMPLATE__ from './rating-participation-group-filter.hbs'; */
/* RESPONSIBLE TEAM: team-reporting */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { REPORTING_FILTER_SELECT_ALL } from 'embercom/lib/reporting/flexible/constants';
import type IntlService from 'ember-intl/services/intl';
import type { SelectedFilter } from 'embercom/services/filter-pattern-service';

interface Args {
  selected: SelectedFilter;
  loadKnownValuesSources: string[];
  analyticsObject: string;
  setSelected: (filterValues: string[], operator?: string) => void;
  removeFilter: () => void;
  initiallyOpened: boolean;
  icon: string;
  type: string;
  name: string;
  isDisabled: boolean;
}

interface Item {
  text: string;
  value: string;
  isSelected: boolean;
}

export default class RatingParticipationGroup extends Component<Args> {
  @service declare appService: AppService;
  @service declare intercomEventService: $TSFixMe;
  @service declare intl: IntlService;
  @tracked selectedDropdownItems = this.args.selected.values || [];
  @tracked operator = this.args.selected.operator || 'category';
  @tracked label = this.currentLabel;
  constructor(owner: unknown, args: $TSFixMe) {
    super(owner, args);
  }

  get participationGroupLabels() {
    return {
      human_only: this.intl.t(
        'components.reporting.custom.chart-builder.filter-bar.rating-participation-group-filter.human_only',
      ),
      'human_and_ai-agent': this.intl.t(
        'components.reporting.custom.chart-builder.filter-bar.rating-participation-group-filter.human_and_ai-agent',
      ),
      human_and_workflow: this.intl.t(
        'components.reporting.custom.chart-builder.filter-bar.rating-participation-group-filter.human_and_workflow',
      ),
      'ai-agent_only': this.intl.t(
        'components.reporting.custom.chart-builder.filter-bar.rating-participation-group-filter.ai-agent_only',
      ),
      workflow_only: this.intl.t(
        'components.reporting.custom.chart-builder.filter-bar.rating-participation-group-filter.workflow_only',
      ),
    } as const;
  }

  get allItems(): Item[] {
    return Object.entries(this.participationGroupLabels).map(([value, text]) => ({
      text,
      value,
      isSelected: this.selectedDropdownItems.includes(value),
    }));
  }

  get selectedParticipationGroups() {
    return this.selectedDropdownItems.filter((item) => item !== REPORTING_FILTER_SELECT_ALL);
  }

  get currentLabel() {
    if (this.selectedParticipationGroups.length === 0) {
      return this.intl.t(
        'components.reporting.custom.chart-builder.filter-bar.rating-participation-group-filter.any',
      );
    }

    let particpationGroups = this.selectedParticipationGroups.map((agent) => {
      return this.participationGroupLabels[agent as keyof typeof this.participationGroupLabels];
    });
    return `${particpationGroups.join(', ')}`;
  }

  @action
  trackAnalytics() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'filtered_participation_rated_group',
      object: this.args.analyticsObject,
      participationGroups: this.selectedParticipationGroups,
      operator: this.operator,
    });
  }

  @action
  removeFilter() {
    this.selectedDropdownItems = [];
    this.args.removeFilter();
  }

  @action
  onClose() {
    if (this.isDestroying) {
      // for some reason onClose gets triggered when you remove the filter
      // even if the dropdown isn't currently open
      return;
    }

    this.label = this.currentLabel;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Custom::ChartBuilder::FilterBar::RatingParticipationGroupFilter': typeof RatingParticipationGroup;
    'reporting/custom/chart-builder/filter-bar/rating-participation-group-filter': typeof RatingParticipationGroup;
  }
}
